/**
 * French message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: vous (locked in l10n/locales.md).
 *
 * Platform terms follow French UI conventions (paramètres, association
 * for pairing, duplication for notification mirroring, effacer for
 * dismiss, thème Clair/Sombre/Automatique). "Glance" is the untranslated
 * feature brand name (glossary D1); "clip" is the D2 loanword.
 *
 * Plural notes: French cardinal categories are {one, many, other} with
 * `many` applying to millions-scale only (never UI counts) — the
 * `selectPlural` calls supply {one, other} and fall back to `other`.
 * French `one` covers BOTH 0 and 1, so "0 résultat" renders singular —
 * correct French, and the reason counts are never hardcoded in `one`.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Connecté",
    statusReconnecting: "Reconnexion…",
    statusDisconnected: "Déconnecté",
    openSettings: "Ouvrir les paramètres",
    pairedFallbackDevice: "votre téléphone",
    publicModeToggleEnable: "Masquer le contenu des notifications",
    publicModeToggleDisable: "Afficher le contenu des notifications",
  },

  notifications: {
    replyAriaLabel: "Répondre",
    replyPlaceholder: "Répondre…",
    replySend: "Envoyer",
    sent: "Envoyé",
    failedToSend: "Échec de l'envoi",
    unread: "Non lu",
    dismiss: "Effacer",
    notificationImageAlt: "Image de la notification",
  },

  conversations: {
    replyAriaLabel: "Répondre",
    replyPlaceholder: "Répondre…",
    replySend: "Envoyer",
    sent: "Envoyé",
    failedToSend: "Échec de l'envoi",
    dismiss: "Effacer la conversation",
    messagesListLabel: "Messages de cette conversation",
    deviceOwner: "Vous",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Aucun message non lu"
        : `${count} ${selectPlural("fr", count, {
            one: "message non lu",
            other: "messages non lus",
          })}`,
  },

  list: {
    today: "Aujourd'hui",
    yesterday: "Hier",
    searchNotifications: "Rechercher des notifications",
    clearSearch: "Effacer la recherche",
    clearAll: "Tout effacer",
    markAllRead: "Tout marquer comme lu",
    groupCheckboxLabel: "Grouper",
    noNotificationsTitle: "Aucune notification pour l'instant",
    noNotificationsDesc:
      "Les nouveaux messages dupliqués depuis votre téléphone s'afficheront ici.",
    noMatchesTitle: "Aucun résultat",
    notificationsList: "Notifications",
    privacyAppList: "Notifications par application",
    clear: "Effacer",
    clearMatches: (count: number): string =>
      `Effacer ${count} ${selectPlural("fr", count, { one: "résultat", other: "résultats" })}`,
    noMatchesDesc: (query: string): string => `Aucune notification ne correspond à « ${query} ».`,
    appNotificationsGroup: (appName: string): string => `Notifications ${appName}`,
    dismissAllFrom: (appName: string): string => `Tout effacer de ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("fr", count, { one: "message", other: "messages" })
          : kind === "calls"
            ? selectPlural("fr", count, { one: "appel", other: "appels" })
            : selectPlural("fr", count, { one: "notification", other: "notifications" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "effacer",
    kbdNavigate: "naviguer",
    kbdOrSeparator: "ou",
  },

  settings: {
    back: "Retour aux notifications",
    settings: "Paramètres",
    general: "Général",
    theme: "Thème",
    themeAuto: "Automatique",
    themeLight: "Clair",
    themeDark: "Sombre",
    language: "Langue",
    languageSystemDefault: "Par défaut du système",
    notificationsSection: "Notifications",
    osNotifications: "Notifications système",
    callNotifications: "Notifications d'appel",
    privacySecurity: "Confidentialité",
    analyticsToggle: "Statistiques d'utilisation anonymes",
    analyticsToggleDescription:
      "Uniquement des totaux et des étapes clés — jamais vos notifications.",
    statusConnected: "Connecté",
    statusReconnecting: "Reconnexion…",
    statusDisconnected: "Déconnecté",
    disconnectFromPhone: "Se déconnecter du téléphone",
    disconnectConfirm: "Êtes-vous sûr ? Cela dissociera votre appareil.",
    yesDisconnect: "Oui, déconnecter",
    cancel: "Annuler",
    version: "Version",
    encryption: "Chiffrement",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Besoin d'aide ? Écrivez à __EMAIL__",
    disconnectFailed: "Échec de la déconnexion — veuillez réessayer",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Déconnecté, mais le nettoyage local a échoué : ${errorMsg}. Veuillez recharger la page.`,
    disconnectLocalCleanupFailed:
      "Déconnecté, mais le nettoyage local a échoué. Veuillez recharger la page.",
  },

  pairing: {
    enterCodeInstruction: "Saisissez ce code dans l'application Reflecto",
    pairedSuccessfully: "Association réussie !",
    pairingFailed: "Échec de l'association",
    requestingPairingCode: "Demande du code d'association…",
    codeExpired: "Code expiré",
    tryAgain: "Réessayer",
    failedToStartPairing: "Impossible de démarrer l'association",
    installHeading: "Vous n'avez pas encore l'application ?",
    installScanHint: "Scannez avec l'appareil photo de votre téléphone",
    installQrLabel: "QR code pour installer Reflecto sur votre téléphone",
    capabilitiesHeading: "Une fois associé, vous pourrez :",
    capabilityMirror: "Voir ici les notifications de votre téléphone",
    capabilityReply: "Répondre sans prendre votre téléphone",
    capabilityShare: "Partager le presse-papiers et des fichiers dans les deux sens",
    encryptionNote: "Chiffré de bout en bout. Le serveur ne voit jamais votre contenu.",
  },

  dnd: {
    title: "Ne pas déranger",
    description: "Suspendez les notifications sur cet appareil pendant l'heure à venir.",
    snooze1h: "Suspendre 1 heure",
    snoozeActiveUntil: (formattedTime: string): string => `Actif jusqu'à ${formattedTime}`,
    cancelSnooze: "Annuler",
  },

  connectors: {
    title: "Services connectés",
    emptyBody:
      "Aucun service connecté pour l'instant. Connectez ChatGPT ou Claude depuis leurs paramètres de connecteur, puis validez le code sur votre téléphone.",
    scopeSend: "Peut envoyer des notifications",
    added: (date: string): string => `Ajouté ${date}`,
    lastUsed: (relative: string): string => `Dernière utilisation ${relative}`,
    neverUsed: "Jamais utilisé",
    revoke: "Déconnecter",
    revokeConfirm: (label: string): string =>
      `Déconnecter ${label} ? Il perdra l'accès immédiatement.`,
    revokeAction: "Déconnecter",
    revokeConfirmLabel: (label: string): string => `Déconnecter ${label}`,
    cancel: "Annuler",
    loadError: "Impossible de charger les services connectés. Veuillez réessayer.",
    revokeError: "Impossible de déconnecter le service. Veuillez réessayer.",
    retry: "Réessayer",
  },

  safetyNumber: {
    rowLabel: "Vérifier le chiffrement",
    rowSublabel: "Comparez les numéros de sécurité avec votre téléphone",
    cardTitle: "Numéro de sécurité",
    caption: (deviceLabel: string): string =>
      `Comparez avec le numéro affiché sur ${deviceLabel}. Si les deux correspondent, votre chiffrement de bout en bout est intact.`,
    repairNote: "Ce numéro change lorsque vous associez à nouveau un appareil.",
    copy: "Copier",
    copied: "Copié",
    maskedHint: "Masqué alors que le mode de confidentialité est activé",
  },

  feedback: {
    title: "Envoyer des commentaires",
    close: "Fermer",
    messageLabel: "Votre message",
    messagePlaceholder: "Un bug, une idée, ou quelque chose qui cloche ? Dites-le-nous.",
    categoryLabel: "Catégorie",
    categoryBug: "Bug",
    categoryIdea: "Idée",
    categoryOther: "Autre",
    emailLabel: "E-mail (facultatif, uniquement si vous souhaitez une réponse)",
    emailPlaceholder: "vous@exemple.fr",
    emailHelp: "Nous ne vous ajouterons à aucune liste.",
    disclosure:
      "Votre message est envoyé au serveur de Reflecto et enregistré dans notre outil de suivi interne. Nous y joignons la version de l'application et un identifiant d'appareil attribué lors de l'association pour relier les rapports entre eux. Nous ne voyons ni vos notifications, ni vos contacts, ni aucune donnée personnelle. L'e-mail est facultatif — indiquez-le uniquement si vous souhaitez une réponse. Nous ne vous ajouterons à aucune liste.",
    submit: "Envoyer des commentaires",
    sending: "Envoi…",
    success: "Merci — lu par un humain, généralement sous 24 h",
    error: "Impossible d'envoyer. Veuillez réessayer.",
    done: "Terminé",
    sendFeedback: "Envoyer des commentaires",
  },

  uninstall: {
    headline: "L'extension Reflecto est désinstallée",
    body: "Il n'y a jamais eu de compte, et l'extension conservait votre association sur cet ordinateur : il ne reste donc rien à nettoyer.",
    ask: "Si vous avez trente secondes, nous aimerions savoir ce qui n'a pas fonctionné.",
    categoryLegend: "De quel type de commentaire s'agit-il ?",
    categoryBug: "Quelque chose ne marchait plus",
    categoryIdea: "Il manquait quelque chose dont j'avais besoin",
    categoryOther: "Autre chose",
    messageLabel: "Qu'est-ce qui n'a pas fonctionné ?",
    messagePlaceholder: "Les notifications ont cessé d'arriver au bout de quelques heures.",
    messageRequired: "Écrivez d'abord une phrase, puis envoyez.",
    charactersLeft: (count: number): string => `${count} caractères restants`,
    emailLabel: "E-mail (facultatif)",
    emailPlaceholder: "vous@exemple.fr",
    emailHelp: "Utilisé uniquement pour vous répondre. Rien d'autre.",
    submit: "Envoyer des commentaires",
    sending: "Envoi…",
    retry: "Réessayer",
    privacyNote:
      "Ce formulaire n'est lié à aucun appareil. Il ne contient aucune de vos notifications, ni votre presse-papiers, ni vos fichiers. L'e-mail est facultatif et sert uniquement à vous répondre.",
    success: "Merci. Un humain lit chacun de ces messages.",
    errorGeneric: "L'envoi a échoué. Votre message est toujours là.",
    errorRateLimited:
      "Vous avez envoyé plusieurs messages récemment. Réessayez dans environ une heure. Votre texte reste ici.",
    footerWebApp: "L'application web Reflecto fonctionne toujours sans l'extension.",
    footerWebAppLink: "Ouvrir l'application web",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Fermer",
    aiSummaryTag: "Résumé IA",
    errorAi: "Impossible de générer un résumé pour le moment.",
    retryCta: "Réessayer",
    downloadHeading: "Recevez des résumés IA",
    downloadBody:
      "Rattrapez ce que vous avez manqué grâce à un résumé IA généré sur l'appareil. Tout s'exécute sur votre appareil.",
    downloadCta: "Télécharger le modèle",
    downloadPrivacyNote: "Un téléchargement unique. Le serveur ne voit jamais vos notifications.",
    downloadingHeading: "Téléchargement du modèle…",
    caughtUp: "Vous êtes à jour — rien ne requiert votre attention pour le moment.",
    needsYou: "À traiter",
    quiet: "Le reste",
    scanning: (pending: number): string => `Résumé en cours : ${pending}…`,
    jump: "Ouvrir la notification",
    affordanceReply: "Répondre",
    affordanceOpen: "Ouvrir",
    affordanceCopyOtp: "Copier le code",
    buttonLabel: "Glance",
  },

  update: {
    title: "Mise à jour disponible",
    description: "Une nouvelle version de Reflecto est prête. Rechargez pour mettre à jour.",
    reload: "Recharger",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Nous avons nommé cet appareil « ${name} ».`,
    cardLooksGood: "Ça me va",
    cardEdit: "Modifier",
    cardAriaLabel: "Confirmer le nom de l'appareil",
    renameTitle: "Renommer cet appareil",
    renameLabel: "Nom de l'appareil",
    renameSave: "Enregistrer",
    renameCancel: "Annuler",
    renameFailed: "Impossible de renommer. Veuillez réessayer.",
  },

  collision: {
    title: (name: string): string => `Vous avez déjà un « ${name} »`,
    subtitle: "Comment appeler celui-ci ?",
    chipWork: (name: string): string => `Travail ${name}`,
    chipHome: (name: string): string => `Maison ${name}`,
    chipPersonal: (name: string): string => `Personnel ${name}`,
    customLabel: "Nom personnalisé",
    save: "Enregistrer",
    useAnyway: "Utiliser quand même le nom d'origine",
    saveFailed: "Impossible d'enregistrer le nouveau nom. Veuillez réessayer.",
    ariaLabel: "Résoudre le nom d'appareil en double",
  },

  clipboard: {
    title: "Presse-papiers",
    emptyTitle: "Aucun clip pour l'instant",
    empty: "Les clips de vos autres appareils s'affichent ici. Appuyez pour copier.",
    from: (label: string): string => `de ${label}`,
    to: (label: string): string => `à ${label}`,
    copy: "Copier dans le presse-papiers",
    copied: "Copié",
    reveal: "Afficher",
    hide: "Masquer",
    sensitiveChip: "Sensible",
    cloudChip: "via votre token d'API",
    maskedHint: "Contenu masqué — appuyez sur « Afficher » pour le voir",
    delete: "Supprimer le clip",
    sendPlaceholder: "Collez le texte à envoyer…",
    sendCta: "Envoyer",
    sendSensitiveToggle: "Marquer comme sensible",
    sendSuccess: (n: number): string =>
      `Envoyé à ${n} ${selectPlural("fr", n, { one: "appareil", other: "appareils" })}.`,
    sendPartial: (n: number, total: number): string =>
      `Envoyé à ${n} ${selectPlural("fr", total, {
        one: "appareil sur",
        other: "appareils sur",
      })} ${total}.`,
    sendNoDevices: "Aucun appareil associé à qui envoyer.",
    sendFailed: "Impossible d'envoyer. Veuillez réessayer.",
    sendEmpty: "Rien à envoyer.",
    dockRecentHeading: "Récents",
    dockEmptyClipboard: "Votre presse-papiers est vide.",
    clearAll: "Tout effacer",
    clearAllConfirm: "Oui, tout effacer",
    clearAllCancel: "Annuler",
    shareTitle: "Envoyer à vos appareils",
    shareReview:
      "Vérifiez ce que vous envoyez. Le contenu part vers vos appareils associés, chiffré de bout en bout.",
    shareSend: "Envoyer",
    shareCancel: "Annuler",
    shareNotPaired: "Associez d'abord un appareil pour envoyer du texte partagé.",
    shareOpenApp: "Ouvrir Reflecto",
    pasteFromClipboard: "Coller depuis le presse-papiers",
    readFailed: "Impossible de lire le presse-papiers — collez le texte ci-dessous.",
  },
  install: {
    title: "Installer Reflecto",
    body: "Ajoutez-le à votre écran d'accueil pour un accès plus rapide.",
    cta: "Installer",
    dismissAria: "Ignorer l'invite d'installation",
  },
  nav: {
    home: "Accueil",
    clipboard: "Presse-papiers",
    files: "Fichiers",
    ariaLabel: "Navigation principale",
    fileHiddenPublicMode: "Masqué tant que le mode privé est activé.",
  },
  common: {
    loading: "Chargement",
  },
  errors: {
    notPaired: "Non associé",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} sur ${os}`,
    webFallback: "Navigateur web",
  },
  palette: {
    placeholder: "Tapez une commande…",
    noResults: "Aucune commande correspondante",
    ariaLabel: "Palette de commandes",
    clearAll: "Effacer toutes les notifications",
    glance: "Résumé Glance",
    cancelSnooze: "Annuler la suspension",
    copyClip: "Copier le dernier clip reçu",
  },

  fileTransfer: {
    sendTitle: "Envoyer un fichier",
    sendChooseFile: "Choisir un fichier",
    sendDeviceLabel: "Envoyer à",
    sendPhoneFallbackName: "Téléphone",
    send: "Envoyer le fichier",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Préparation…" : `Préparation — ${pct}%`,
    sendDelivering: "Transmission à votre téléphone…",
    sendWaitingAccept: "En attente d'acceptation par votre téléphone…",
    sendWaitingAppOpen: "Ouvrez Reflecto sur votre téléphone pour accepter",
    sendWaitingAppOpenHint:
      "Votre téléphone ne peut pas recevoir de notifications push : ce fichier n'apparaîtra qu'une fois Reflecto ouvert.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Envoi de la partie ${partNumber} sur ${partCount}…`,
    sendDone: "Envoyé.",
    sendFailedRejected: "Votre téléphone a refusé le fichier.",
    sendFailedNoAnswer: "Pas de réponse de votre téléphone.",
    sendFailedUploadFailed: "Échec du téléversement. Veuillez réessayer.",
    sendFailedCancelled: "Annulé.",
    sendFailedGeneric: "Une erreur s'est produite. Veuillez réessayer.",
    sendTooLarge: (maxMiB: number): string =>
      `Le fichier est trop volumineux (max. ${maxMiB} MiB).`,
    sendKeepOpenHint: "Gardez cet onglet ouvert jusqu'à la fin du transfert.",
    sendAnother: "Envoyer un autre",
    sendNoDevice: "Associez d'abord un téléphone pour envoyer des fichiers.",
    sendCancel: "Annuler",
    sendPeerProgress: (chunksDone: number): string =>
      `Le téléphone a reçu ${chunksDone} fragments.`,

    receiveTitle: "Fichiers entrants",
    receiveOfferFrom: (sender: string): string => `${sender} veut vous envoyer un fichier`,
    receiveAccept: "Accepter",
    receiveDecline: "Refuser",
    receiveReceiving: (n: number, total: number): string =>
      `Réception de la partie ${n} sur ${total}…`,
    receiveDone: "Enregistré.",
    receiveFailed: "Échec du transfert.",
    receiveCancelled: "Annulé.",
    receiveCancel: "Annuler",

    errorChunkAuthFail: "Échec du transfert — impossible de vérifier les données. Réessayez.",
    errorHashMismatch:
      "Échec du transfert — le fichier n'a pas passé le contrôle d'intégrité. Réessayez.",
    errorStorageFull: "Espace de stockage insuffisant pour ce transfert.",
    errorExpired: "Le transfert a expiré.",
    errorQuotaExceeded: "Limite quotidienne de transferts atteinte — réessayez demain.",
    errorInternal: "Échec du transfert — une erreur s'est produite.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct} %`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Ignorer",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Progression du transfert",
    outgoing: (n: number): string => `Sortants (${n})`,
    incoming: (n: number): string => `Entrants (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Tout accepter",
    dropTitle: "Glissez-déposez ici",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `Fichiers jusqu’à ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Glissez-déposez ici ou choisissez un fichier à envoyer",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Direct",
    transportDirectHint: "Envoyé d’appareil à appareil — jamais stocké sur nos serveurs.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Via le serveur",
    transportRelayHint:
      "Relayé par nos serveurs, toujours chiffré de bout en bout — nous ne pouvons pas le lire.",
    directBadge: "Direct",
  },
  time: {
    justNow: "à l'instant",
    minutesAgo: (count: number): string => `il y a ${count} min`,
    hoursAgo: (count: number): string => `il y a ${count} h`,
  },
} satisfies MessagesShape;
